// src/Vpis/Vpis.js
import React, { useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { apiFetch, setAccessToken } from "../api";
import "./Vpis.css";

export default function Vpis() {
    const navigate = useNavigate();
    const location = useLocation();

    const [ePosta, nastaviEPosto] = useState("");
    const [geslo, nastaviGeslo] = useState("");
    const [pokaziGeslo, nastaviPokaziGeslo] = useState(false);
    const [capsLock, nastaviCapsLock] = useState(false);
    const [status, nastaviStatus] = useState("idle"); 
    const [sporocilo, nastaviSporocilo] = useState("");
    const napakaRef = useRef(null);

    const ePostaVeljavna = useMemo(
        () => /\S+@\S+\.\S+/.test(ePosta),
        [ePosta]
    );

    const gesloVeljavno = geslo.length >= 12;
    const lahkoPosljem = ePostaVeljavna && gesloVeljavno && status !== "loading";

    function obTipkanjuGesla(e) {
        if (typeof e.getModifierState === "function") {
            nastaviCapsLock(e.getModifierState("CapsLock"));
        }
    }

    async function obOddaji(e) {
        e.preventDefault();
        if (!lahkoPosljem) return;

        nastaviStatus("loading");
        nastaviSporocilo("");

        try {
            const res = await apiFetch(
                "/api/auth/vpis",
                {
                    method: "POST",
                    body: {
                        ePosta: ePosta.trim(),
                        geslo: geslo
                    }
                },
                { autoRedirectOn401: false }
            );

            if (!res.ok) {
                let text = "Neveljaven e-poštni naslov ali geslo.";
                if (res.status === 423) {
                    text = "Račun je začasno zaklenjen. Poskusite znova kasneje.";
                }
                try {
                    const data = await res.json();
                    text = data?.message || text;
                } catch {

                }

                nastaviStatus("error");
                nastaviSporocilo(text);
                setTimeout(() => napakaRef.current?.focus(), 0);
                return;
            }

            const data = await res.json().catch(() => ({}));
            if (data?.accessToken) setAccessToken(data.accessToken);

            nastaviStatus("success");
            nastaviGeslo("");

            const from = location.state?.from?.pathname || "/profil";
            navigate(data?.redirect || from, { replace: true });
        } catch {
            nastaviStatus("error");
            nastaviSporocilo("Napaka pri povezavi s strežnikom.");
            setTimeout(() => napakaRef.current?.focus(), 0);
        }
    }

    return (
        <div className="flp-wrap">
            <form
                className="flp-card"
                onSubmit={obOddaji}
                autoComplete="on"
                noValidate
            >
                <header className="flp-header">
                    <div className="flp-logo" aria-label="IZVRŠITELJ">
                        <img
                            className="flp-logo-img"
                            src="/izvrsitelj.png"
                            alt="IZVRŠITELJ"
                            loading="eager"
                        />
                    </div>
                    <div className="flp-legend">VPIS IZVRŠITELJA</div>
                </header>

                <label className="flp-label" htmlFor="flp-email">
                    E-pošta
                </label>
                <div className="flp-field">
                    <input
                        id="flp-email"
                        className="flp-input"
                        type="email"
                        inputMode="email"
                        placeholder="vi@email.si"
                        value={ePosta}
                        onChange={(e) => nastaviEPosto(e.target.value)}
                        required
                        aria-invalid={!!ePosta && !ePostaVeljavna}
                        autoComplete="username"
                        autoCapitalize="off"
                        autoCorrect="off"
                        spellCheck={false}
                    />
                </div>

                <label className="flp-label" htmlFor="flp-password">
                    Geslo
                </label>
                <div className="flp-field flp-has-icon">
                    <input
                        id="flp-password"
                        className="flp-input"
                        type={pokaziGeslo ? "text" : "password"}
                        placeholder="************"
                        value={geslo}
                        onChange={(e) => nastaviGeslo(e.target.value)}
                        onKeyDown={obTipkanjuGesla}
                        onKeyUp={obTipkanjuGesla}
                        minLength={12}
                        required
                        autoComplete="current-password"
                    />

                    <button
                        type="button"
                        className="flp-eye"
                        onClick={() => nastaviPokaziGeslo((s) => !s)}
                        aria-label={pokaziGeslo ? "Skrij geslo" : "Pokaži geslo"}
                        aria-pressed={pokaziGeslo}
                        aria-controls="flp-password"
                        title={pokaziGeslo ? "Skrij geslo" : "Pokaži geslo"}
                    >
                        {pokaziGeslo ? (
                            <svg
                                viewBox="0 0 24 24"
                                width="18"
                                height="18"
                                aria-hidden="true"
                                focusable="false"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20C5 20 1 12 1 12a21.07 21.07 0 0 1 5.06-6.87" />
                                <path d="M9.88 9.88A3 3 0 1 0 14.12 14.12" />
                                <path d="M12 5c7 0 11 7 11 7a20.87 20.87 0 0 1-3.23 4.31" />
                                <line x1="1" y1="1" x2="23" y2="23" />
                            </svg>
                        ) : (
                            <svg
                                viewBox="0 0 24 24"
                                width="18"
                                height="18"
                                aria-hidden="true"
                                focusable="false"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
                                <circle cx="12" cy="12" r="3" />
                            </svg>
                        )}
                    </button>
                </div>

                {capsLock && (
                    <div className="flp-caps">Tipka Caps Lock je vklopljena</div>
                )}

                <button
                    type="submit"
                    className={`flp-button ${lahkoPosljem ? "" : "flp-disabled"}`}
                    disabled={!lahkoPosljem}
                >
                    {status === "loading" ? "Prijavljanje …" : "Prijava"}
                </button>

                {status === "error" && (
                    <div
                        className="flp-alert error"
                        role="alert"
                        aria-live="assertive"
                        tabIndex={-1}
                        ref={napakaRef}
                    >
                        {sporocilo}
                    </div>
                )}

                <footer className="flp-footer">
                    <span>© portal</span>
                </footer>
            </form>
        </div>
    );
}